/**
 * Error Mapper Utility
 * Feature: 034-global-error-handling
 * Constitution: v1.11.0 - Principle XI (Web Application Architecture)
 * 
 * @description Maps API errors to frontend-friendly format
 * @implements FR-010, FR-011, FR-012, FR-013, FR-014, FR-015
 * @author Peter Dobler
 * @created 2026-01-20
 */

import { AxiosError, AxiosResponse } from 'axios';
import {
  ErrorCodes,
  ErrorCode,
  ApiErrorResponse,
  ErrorSeverity,
  HTTP_STATUS_TO_ERROR_CODE,
  ERROR_MESSAGES,
  RETRYABLE_STATUS_CODES,
  NON_RETRYABLE_STATUS_CODES,
} from '../../../shared/types/errors';
import {
  FrontendError,
  ErrorDisplayConfig,
  HTTP_ERROR_MESSAGES,
  NETWORK_ERROR_CONFIG,
  UNKNOWN_ERROR_CONFIG,
} from '../types/error';

/**
 * Check if error is an AxiosError
 */
export function isAxiosError(error: unknown): error is AxiosError {
  return error instanceof Error && 'isAxiosError' in error;
}

/**
 * Check if response data matches ApiErrorResponse format
 */
export function isApiErrorResponse(data: unknown): data is ApiErrorResponse {
  return (
    typeof data === 'object' &&
    data !== null &&
    'success' in data &&
    'error' in data &&
    typeof (data as ApiErrorResponse).error?.code === 'string'
  );
}

/**
 * Check if an error is retryable based on status code
 * FR-029: Classify retryable vs non-retryable errors
 */
export function isRetryableError(statusCode: number): boolean {
  // Explicit non-retryable
  if (NON_RETRYABLE_STATUS_CODES.includes(statusCode as any)) {
    return false;
  }
  // Explicit retryable
  if (RETRYABLE_STATUS_CODES.includes(statusCode as any)) {
    return true;
  }
  // Default: 5xx are retryable, others are not
  return statusCode >= 500 && statusCode < 600;
}

/**
 * Check if error is a network error (no response)
 */
export function isNetworkError(error: AxiosError): boolean {
  return !error.response && !error.code?.startsWith('ERR_BAD');
}

/**
 * Extract correlation ID from response headers
 * FR-055: Correlation ID extraction
 */
export function extractCorrelationId(response: AxiosResponse | undefined): string | undefined {
  if (!response?.headers) return undefined;
  
  // Check common correlation ID header names
  return (
    response.headers['x-correlation-id'] ||
    response.headers['x-request-id'] ||
    response.headers['correlation-id'] ||
    response.headers['request-id']
  );
}

/**
 * Get error code from HTTP status
 */
export function getErrorCodeFromStatus(status: number): ErrorCode {
  return HTTP_STATUS_TO_ERROR_CODE[status] || ErrorCodes.UNKNOWN_ERROR;
}

/**
 * Get severity from HTTP status
 */
export function getSeverityFromStatus(status: number): ErrorSeverity {
  if (status >= 500) return ErrorSeverity.CRITICAL;
  if (status === 401 || status === 403) return ErrorSeverity.HIGH;
  if (status === 404 || status === 400) return ErrorSeverity.MEDIUM;
  return ErrorSeverity.LOW;
}

/**
 * Map AxiosError to FrontendError
 * FR-010-015: Error classification and mapping
 */
export function mapAxiosError(error: AxiosError): FrontendError {
  // Network error (no response)
  if (isNetworkError(error)) {
    return {
      code: ErrorCodes.NETWORK_ERROR,
      message: ERROR_MESSAGES[ErrorCodes.NETWORK_ERROR],
      statusCode: 0,
      isRetryable: true,
      severity: ErrorSeverity.HIGH,
      originalError: error,
    };
  }

  const response = error.response;
  const statusCode = response?.status || 0;
  const data = response?.data;

  // Extract API error response if available
  let apiError: Partial<ApiErrorResponse['error']> = {};
  if (isApiErrorResponse(data)) {
    apiError = data.error;
  }

  // Build frontend error
  const errorCode = apiError.code as ErrorCode || getErrorCodeFromStatus(statusCode);
  const frontendError: FrontendError = {
    code: errorCode,
    message: apiError.message || ERROR_MESSAGES[errorCode] || 'An error occurred',
    details: apiError.details as string | undefined,
    statusCode,
    correlationId: apiError.correlationId || extractCorrelationId(response),
    referenceId: apiError.referenceId,
    isRetryable: isRetryableError(statusCode),
    severity: getSeverityFromStatus(statusCode),
    originalError: error,
  };

  return frontendError;
}

/**
 * Get display configuration for an error
 */
export function getErrorDisplayConfig(error: FrontendError): ErrorDisplayConfig {
  // Use predefined config if available
  const config = HTTP_ERROR_MESSAGES[error.statusCode];
  
  if (config) {
    // Enhance with specific error details if available
    return {
      ...config,
      message: error.message || config.message,
    };
  }

  // Network error
  if (error.code === ErrorCodes.NETWORK_ERROR) {
    return NETWORK_ERROR_CONFIG;
  }

  // Fallback
  return {
    ...UNKNOWN_ERROR_CONFIG,
    message: error.message || UNKNOWN_ERROR_CONFIG.message,
  };
}

/**
 * Format error for user display
 * FR-016: Include reference ID for support
 */
export function formatErrorMessage(error: FrontendError): string {
  let message = error.message;
  
  // Add reference ID for support in production
  if (error.referenceId && error.severity === ErrorSeverity.CRITICAL) {
    message += ` (Reference: ${error.referenceId})`;
  }
  
  return message;
}

/**
 * Map any error to FrontendError
 */
export function mapError(error: unknown): FrontendError {
  // AxiosError
  if (isAxiosError(error)) {
    return mapAxiosError(error);
  }

  // Standard Error
  if (error instanceof Error) {
    return {
      code: ErrorCodes.UNKNOWN_ERROR,
      message: error.message,
      statusCode: 0,
      isRetryable: false,
      severity: ErrorSeverity.MEDIUM,
      originalError: error,
    };
  }

  // Unknown
  return {
    code: ErrorCodes.UNKNOWN_ERROR,
    message: 'An unexpected error occurred',
    statusCode: 0,
    isRetryable: false,
    severity: ErrorSeverity.LOW,
  };
}
